import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'
import {
  getEmployeesApi,
  getEmployeeCircleProfileApi,
  updateEmployeeCircleProfileApi,
  deleteEmployeeCircleProfileApi,
  type EmployeeUser,
  type EmployeeCircleProfile,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BRAND_MINT } from '@/lib/utils'
import {
  Loader2,
  SaveIcon,
  XIcon,
  UserCogIcon,
  SearchIcon,
  UsersIcon,
  RotateCcwIcon,
} from 'lucide-react'

/** Small signature lockup: mint slash + dot, used on section headings. */
function MintSlashDot() {
  return (
    <div className="flex items-center gap-[3px]" aria-hidden="true">
      <span
        className="inline-block w-[2px] h-3.5"
        style={{ backgroundColor: BRAND_MINT }}
      />
      <span
        className="inline-block rounded-full"
        style={{ width: '4px', height: '4px', backgroundColor: BRAND_MINT }}
      />
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <MintSlashDot />
      <h2 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-[0.12em] select-none">
        {children}
      </h2>
    </div>
  )
}

/** Derive a two-letter initials avatar from a display name. */
function initialsFor(firstName: string | null | undefined, lastName: string | null | undefined): string {
  return ((firstName?.charAt(0) ?? '') + (lastName?.charAt(0) ?? '')).toUpperCase() || '?'
}

export const Route = createFileRoute('/dashboard/employee-settings')({
  component: () => {
    const { user } = useAuth()
    const [employees, setEmployees] = useState<EmployeeUser[]>([])
    const [selectedId, setSelectedId] = useState<number | ''>('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [employeeCircleProfile, setEmployeeCircleProfile] = useState<EmployeeCircleProfile | null>(null)
    const [search, setSearch] = useState('')
    const [searchVersion, setSearchVersion] = useState(0)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchRef = useRef('')
    useEffect(() => {
      searchRef.current = search
    }, [search])

    // employerId is read through a ref so the load effect's dep array can stay
    // stable across the `user: null → user` auth-context transition.
    const employerIdRef = useRef<number | null>(user?.orgId ?? user?.employerId ?? null)
    useEffect(() => {
      employerIdRef.current = user?.orgId ?? user?.employerId ?? null
    }, [user?.orgId, user?.employerId])

    useEffect(() => {
      const id = employerIdRef.current
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      getEmployeesApi(id, 1, 50)
        .then((res) => setEmployees(res.data))
        .catch(() => toast.error('Failed to load employees'))
        .finally(() => setLoading(false))
    }, [])

    const mountedRef = useRef(false)

    // Debounce search
    useEffect(() => {
      if (!mountedRef.current) {
        mountedRef.current = true
        return
      }
      const timer = setTimeout(() => {
        setSearchVersion((v) => v + 1)
      }, 300)
      return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
      const id = employerIdRef.current
      if (!id) return
      if (searchVersion === 0) return

      setLoading(true)
      getEmployeesApi(id, 1, 50, searchRef.current)
        .then((res) => setEmployees(res.data))
        .catch(() => toast.error('Failed to load employees'))
        .finally(() => setLoading(false))
    }, [searchVersion])

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setDropdownOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
      if (!selectedId) {
        setEmployeeCircleProfile(null)
        return
      }
      getEmployeeCircleProfileApi(Number(selectedId))
        .then((res) => setEmployeeCircleProfile(res.profile))
        .catch(() => { /* profile may not exist yet */ })
    }, [selectedId])

    const selectedEmployee = employees.find((e) => e.user_id === selectedId)

    const handleSelectEmployee = (emp: EmployeeUser) => {
      setSelectedId(emp.user_id)
      setSearch('')
      setDropdownOpen(false)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!selectedId) return

      setSaving(true)
      try {
        const formData = new FormData(e.currentTarget)
        const maxAmount = formData.get('max_request_amount_allowed') as string
        const maxCircles = formData.get('max_circles_per_user') as string

        const circleParams: Record<string, number | null | string> = {}
        if (maxAmount !== '') {
          circleParams.max_request_amount_allowed = maxAmount ? Math.round(parseFloat(maxAmount) * 100) : null
        }
        if (maxCircles !== '') {
          const parsed = parseInt(maxCircles, 10)
          circleParams.max_circles_per_user = maxCircles ? (isNaN(parsed) ? null : parsed) : null
        }

        if (Object.keys(circleParams).length > 0) {
          await updateEmployeeCircleProfileApi(Number(selectedId), circleParams)
          const refreshedProfile = await getEmployeeCircleProfileApi(Number(selectedId))
          setEmployeeCircleProfile(refreshedProfile.profile)
        }

        toast.success('Employee Circle settings saved')
      } catch (err) {
        toast.error('Failed to save employee settings')
      } finally {
        setSaving(false)
      }
    }

    const handleResetDefaults = async () => {
      if (!selectedId) return
      setResetting(true)
      try {
        await deleteEmployeeCircleProfileApi(Number(selectedId))
        setEmployeeCircleProfile(null)
        toast.success('Employee Circle profile reset to company defaults')
      } catch {
        toast.error('Failed to reset employee Circle profile')
      } finally {
        setResetting(false)
      }
    }

    return (
      <div className="mx-auto max-w-2xl">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">Employee Circle settings</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Override the company-wide Funding Circle settings for individual employees.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* ── Employee selector ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <SectionHeading>Select employee</SectionHeading>

            <div ref={dropdownRef} className="relative">
              {selectedId ? (
                /* Selected employee chip with clear button */
                <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-3 transition-shadow hover:shadow-sm">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10 text-sm font-semibold text-[var(--brand-mint)]">
                    {selectedEmployee
                      ? initialsFor(selectedEmployee.first_name, selectedEmployee.last_name)
                      : '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {selectedEmployee
                        ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
                        : `Employee #${selectedId}`}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {selectedEmployee?.email ?? ''}
                      {selectedEmployee?.role && ` — ${selectedEmployee.role.replace(/_/g, ' ')}`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedId('')
                      setSearch('')
                      setSearchVersion((v) => v + 1)
                    }}
                    className="size-8 p-0 shrink-0"
                    aria-label="Clear selection"
                  >
                    <XIcon className="size-4" />
                  </Button>
                </div>
              ) : (
                /* Search input with search icon */
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="employeeSearch"
                    type="text"
                    placeholder={loading ? 'Loading employees…' : 'Search by name, email, or pseudonym'}
                    value={search}
                    disabled={loading}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setDropdownOpen(true)
                    }}
                    onFocus={() => {
                      if (employees.length > 0) setDropdownOpen(true)
                    }}
                    className="pl-10"
                  />
                </div>
              )}

              {/* Dropdown: loading */}
              {dropdownOpen && loading && !selectedId && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-card shadow-lg">
                  <div className="flex items-center justify-center gap-2 px-4 py-5 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    Searching…
                  </div>
                </div>
              )}

              {/* Dropdown: results */}
              {dropdownOpen && !loading && employees.length > 0 && !selectedId && (
                <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-border bg-card shadow-lg">
                  {employees.map((emp) => (
                    <button
                      key={emp.user_id}
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted"
                      onClick={() => handleSelectEmployee(emp)}
                    >
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10 text-xs font-semibold text-[var(--brand-mint)]">
                        {initialsFor(emp.first_name, emp.last_name)}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">
                          {emp.first_name} {emp.last_name}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">
                          {emp.email} — {emp.role?.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Dropdown: no results */}
              {dropdownOpen && !loading && employees.length === 0 && search && !selectedId && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-card px-4 py-3 shadow-lg">
                  <p className="text-sm text-muted-foreground">No employees found matching "{search}".</p>
                </div>
              )}
            </div>
          </section>

          {/* ── Selected employee settings ── */}
          {selectedId ? (
            <form onSubmit={handleSubmit}>
              <section className="space-y-6 rounded-xl border border-border bg-card p-5">
                <SectionHeading>Circle settings</SectionHeading>

                <p className="text-sm text-muted-foreground">
                  These override the company-wide Funding Circle profile for this employee. Leave a field empty to use the company default.
                </p>

                <div className="grid gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="max_request_amount_allowed">Max request amount</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        id="max_request_amount_allowed"
                        name="max_request_amount_allowed"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Company default"
                        defaultValue={
                          employeeCircleProfile?.max_request_amount_allowed != null
                            ? (employeeCircleProfile.max_request_amount_allowed / 100).toFixed(2)
                            : ''
                        }
                        className="pl-8"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter the amount in your organization's configured currency.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="max_circles_per_user">Max active Circles per user</Label>
                    <Input
                      id="max_circles_per_user"
                      name="max_circles_per_user"
                      type="number"
                      min="1"
                      placeholder="Company default"
                      defaultValue={employeeCircleProfile?.max_circles_per_user ?? ''}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={saving} className="gap-1.5">
                    {saving ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <SaveIcon className="size-4" />
                        Save changes
                      </>
                    )}
                  </Button>

                  {employeeCircleProfile && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={handleResetDefaults}
                      disabled={resetting}
                    >
                      {resetting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <RotateCcwIcon className="size-4" />
                      )}
                      Reset to defaults
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 ml-auto"
                    onClick={() => {
                      setSelectedId('')
                      setSearch('')
                    }}
                  >
                    <XIcon className="size-4" />
                    Cancel
                  </Button>
                </div>
              </section>
            </form>
          ) : (
            /* Empty state when no employee selected */
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card py-16">
              {loading ? (
                <>
                  <Loader2 className="size-8 animate-spin text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">Loading employees…</p>
                </>
              ) : !employerIdRef.current ? (
                <>
                  <UsersIcon className="size-10 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-foreground">No organization</p>
                  <p className="text-sm text-muted-foreground">No organization is associated with your account.</p>
                </>
              ) : (
                <>
                  <UserCogIcon className="size-10 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-foreground">Select an employee</p>
                  <p className="text-sm text-muted-foreground">Choose an employee above to manage their Circle settings.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    )
  },
})
